import React, { useMemo, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useParams, Link } from 'react-router-dom';
import { categories, productsInCategory } from '../../content/products';
import { fitsBackset, hasFingerprint, hasApple, hasWifi } from '../../lib/lockFit';
import { ProductCard, Breadcrumbs, Html } from '../../components/shop/ShopBits';
import usePageMeta from '../../hooks/usePageMeta';
import NotFound from '../NotFound';

// Filters stay client-side (no URL facets — plan §7.4). Under 150 SKUs, no paging.
// Door material is not a filter: what decides fit is the backset (Hugh, 15 Sep 2026).
// The fit rules live in lib/lockFit so these chips agree with the chooser.
const DOOR_FILTERS = [
  ['all', 'Any backset'],
  ['narrow', 'Narrow stile 30–40 mm'],
  ['standard', '50–70 mm (timber, wide aluminium)'],
];
const ENTRY_FILTERS = [
  ['all', 'Any entry'],
  ['fingerprint', 'Fingerprint'],
  ['apple', 'Apple Home'],
  ['wifi', 'Wi-Fi built in'],
];

const CategoryPage = () => {
  const { category } = useParams();
  const cat = categories.find((c) => c.slug === category);
  usePageMeta();
  const [door, setDoor] = useState('all');
  const [entry, setEntry] = useState('all');
  const [brand, setBrand] = useState('all');

  const all = useMemo(() => (cat ? productsInCategory(cat.slug) : []), [cat]);
  const brands = useMemo(() => Array.from(new Set(all.map((p) => p.brand))), [all]);
  // Only offer a filter when something in this lane matches it (no "Sliding" on the deadbolt lane).
  // A lock whose backset is not confirmed yet only shows under "Any backset".
  const matchesDoor = (p, k) => fitsBackset(p, k);
  const matchesEntry = (p, k) => (k === 'fingerprint' ? hasFingerprint(p) : k === 'apple' ? hasApple(p) : hasWifi(p));
  const doorFilters = DOOR_FILTERS.filter(([k]) => k === 'all' || all.some((p) => matchesDoor(p, k)));
  const entryFilters = ENTRY_FILTERS.filter(([k]) => k === 'all' || all.some((p) => matchesEntry(p, k)));
  const list = all.filter((p) => {
    if (door !== 'all' && !matchesDoor(p, door)) return false;
    if (entry !== 'all' && !matchesEntry(p, entry)) return false;
    if (brand !== 'all' && p.brand !== brand) return false;
    return true;
  });

  if (!cat) return <NotFound />;
  const isLocks = all.some((p) => p.fit.doorMaterials.length > 0);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Breadcrumbs crumbs={[{ name: 'Home', path: '/' }, { name: 'Shop', path: '/shop' }, { name: cat.name }]} />
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">{cat.h1}</h1>
          {isLocks && (
            <Link to="/shop/find-my-lock" className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 hover:border-red-400" data-testid="category-find-my-lock">
              Not sure? Find my lock <ArrowRight size={16} />
            </Link>
          )}
        </div>
        <details className="mb-6 max-w-3xl text-sm">
          <summary className="cursor-pointer font-semibold text-gray-700 select-none">About this range</summary>
          <Html html={cat.intro} className="mt-2 [&_p]:text-sm" />
        </details>

        {isLocks && (
          <div className="flex flex-wrap gap-2 mb-8" role="group" aria-label="Filters">
            {doorFilters.length > 2 && doorFilters.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setDoor(k)} className={`px-3 py-1.5 rounded-full text-sm border ${door === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'}`}>
                {label}
              </button>
            ))}
            {doorFilters.length > 2 && entryFilters.length > 1 && <span className="w-px bg-gray-200 mx-1" aria-hidden="true" />}
            {entryFilters.length > 1 && entryFilters.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setEntry(k)} className={`px-3 py-1.5 rounded-full text-sm border ${entry === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'}`}>
                {label}
              </button>
            ))}
            {brands.length > 1 && (
              <select value={brand} onChange={(e) => setBrand(e.target.value)} className="ml-auto rounded-full border border-gray-300 px-3 py-1.5 text-sm bg-white" aria-label="Brand">
                <option value="all">All brands</option>
                {brands.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {list.length === 0 ? (
          <p className="text-gray-700">
            Nothing matches those filters yet.{' '}
            <Link to="/shop/will-it-fit" className="text-red-600 font-semibold">
              Tell us about your door
            </Link>{' '}
            and we will point you at the right lock.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
